/* Admin: School club/group registrations — list + detail with approve/reject + edit.
   Schools submit via the public /register-club form; admins review and manage here. */
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Link, navigate } from "../../lib/router-compat.jsx";
import AdminShell from "../../components/shell/AdminShell.jsx";
import { Button, Card, Badge, Field, Input, EmptyState, Skeleton, ConfirmDialog, Icon, showToast } from "../../ui/index.js";
import { useSchoolGroups } from "../../data/hooks.js";
import { updateSchoolGroupStatus, saveSchoolGroup, deleteSchoolGroup } from "../../data/mutations.js";
import { formatRelative, formatDateTime } from "../../utils/format.js";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const STATUS_LABEL = { pending: "Pending", approved: "Approved", rejected: "Rejected" };
const emptyLeader = () => ({ name: "", email: "", phone: "", position: "", occupation: "" });

/* Banner with the public registration link admins share with schools. */
function RegistrationLinkCard() {
  const url = `${window.location.origin}/register-club`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      showToast({ intent: "success", title: "Link copied", body: "Share it with schools to register a club." });
    } catch {
      showToast({ intent: "warning", title: "Couldn't copy automatically", body: url });
    }
  };
  return (
    <Card style={{ marginBottom: 24, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
      <div className="icon-circle" style={{ flexShrink: 0 }}><Icon.Link size={20} /></div>
      <div style={{ flex: 1, minWidth: 220 }}>
        <div className="t-base" style={{ fontWeight: 600 }}>Registration link</div>
        <div className="t-sm muted" style={{ wordBreak: "break-all" }}>{url}</div>
      </div>
      <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
        <a href={url} target="_blank" rel="noreferrer" className="btn btn--ghost btn--sm"><Icon.ExternalLink size={14} />Open</a>
        <Button variant="secondary" size="sm" onClick={copy}><Icon.Link size={14} />Copy link</Button>
      </div>
    </Card>
  );
}

export function AdminSchoolGroupsListPage() {
  const { groups, loading } = useSchoolGroups();
  const [filter, setFilter] = useState("all");
  const filtered = groups.filter((g) => (filter === "all" ? true : g.status === filter));
  const count = (k) => (k === "all" ? groups.length : groups.filter((g) => g.status === k).length);

  return (
    <AdminShell>
      <div className="admin-main__header">
        <div>
          <h1 className="t-3xl">School groups</h1>
          <p>Clubs registered by schools. Review pending submissions and manage approved groups.</p>
        </div>
      </div>
      <RegistrationLinkCard />
      <div className="chips" style={{ marginBottom: 24 }}>
        {[["all", "All"], ["pending", "Pending"], ["approved", "Approved"], ["rejected", "Rejected"]].map(([k, label]) => (
          <button key={k} className={`chip ${filter === k ? "active" : ""}`} onClick={() => setFilter(k)}>{label} ({count(k)})</button>
        ))}
      </div>
      {loading ? <Skeleton h={240} /> : filtered.length === 0 ? (
        <Card><EmptyState icon={<Icon.School size={40} />} title={groups.length === 0 ? "No groups registered yet." : "No groups match this filter."} body={groups.length === 0 ? "Share the registration link with schools so they can sign up their clubs." : "Try a different filter."} /></Card>
      ) : (
        <table className="table">
          <thead><tr><th>School</th><th style={{ width: 110 }}>Members</th><th>Meeting days</th><th style={{ width: 110 }}>Status</th><th style={{ width: 130 }}>Submitted</th><th className="table__actions">Actions</th></tr></thead>
          <tbody>
            {filtered.map((g) => (
              <tr key={g.id} className="clickable" onClick={() => navigate(`/admin/groups/${g.id}`)}>
                <td style={{ fontWeight: 600 }}>{g.schoolName}</td>
                <td>{g.memberCount}</td>
                <td className="t-sm muted">{(g.meetingDays || []).join(", ") || "—"}</td>
                <td><Badge variant={g.status}>{STATUS_LABEL[g.status] || g.status}</Badge></td>
                <td className="t-sm muted">{formatRelative(g.submittedAt)}</td>
                <td className="table__actions" onClick={(e) => e.stopPropagation()}><Button variant="ghost" size="sm" onClick={() => navigate(`/admin/groups/${g.id}`)}>View</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </AdminShell>
  );
}

function DetailRow({ label, value, bold }) {
  return (
    <div>
      <div className="t-xs muted" style={{ textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{label}</div>
      <div className="t-base" style={{ fontWeight: bold ? 700 : 500 }}>{value}</div>
    </div>
  );
}

export function AdminSchoolGroupDetailPage() {
  const { groupId } = useParams();
  const { groups, loading } = useSchoolGroups();
  const [showDelete, setShowDelete] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const group = groups.find((g) => g.id === groupId);

  // Seed the edit draft from the live record whenever we enter edit mode.
  useEffect(() => {
    if (editing && group && !draft) {
      setDraft({
        schoolName: group.schoolName || "",
        memberCount: String(group.memberCount ?? ""),
        meetingDays: group.meetingDays || [],
        leaders: (group.leaders && group.leaders.length ? group.leaders : [emptyLeader()]).map((l) => ({ ...emptyLeader(), ...l })),
      });
    }
  }, [editing, group, draft]);

  if (loading) return <AdminShell><Skeleton h={300} /></AdminShell>;
  if (!group) return <AdminShell><EmptyState title="Group not found" body="It may have been deleted." /></AdminShell>;

  const setStatus = async (status) => {
    try { await updateSchoolGroupStatus(group.id, status); showToast({ intent: "success", title: status === "approved" ? "Group approved" : status === "rejected" ? "Group rejected" : "Status updated" }); }
    catch (err) { showToast({ intent: "danger", title: "Couldn't update", body: err.message }); }
  };
  const doDelete = async () => {
    try { await deleteSchoolGroup(group.id); showToast({ intent: "success", title: "Group deleted" }); navigate("/admin/groups"); }
    catch (err) { showToast({ intent: "danger", title: "Couldn't delete", body: err.message }); }
  };

  const startEdit = () => { setDraft(null); setEditing(true); };
  const cancelEdit = () => { setEditing(false); setDraft(null); };

  const saveEdit = async () => {
    if (!draft.schoolName.trim()) { showToast({ intent: "warning", title: "School name is required." }); return; }
    const count = Number(draft.memberCount);
    if (Number.isNaN(count) || count < 0) { showToast({ intent: "warning", title: "Enter a valid member count." }); return; }
    const cleanLeaders = draft.leaders
      .map((l) => ({ name: l.name.trim(), email: l.email.trim(), phone: l.phone.trim(), position: l.position.trim(), occupation: l.occupation.trim() }))
      .filter((l) => l.name || l.email || l.phone || l.position);
    if (cleanLeaders.length === 0) { showToast({ intent: "warning", title: "Add at least one leadership contact." }); return; }
    setSaving(true);
    try {
      await saveSchoolGroup(group.id, {
        schoolName: draft.schoolName.trim(),
        memberCount: Math.round(count),
        meetingDays: draft.meetingDays,
        leaders: cleanLeaders,
      });
      showToast({ intent: "success", title: "Changes saved" });
      setEditing(false);
      setDraft(null);
    } catch (err) {
      showToast({ intent: "danger", title: "Couldn't save", body: err.message });
    } finally {
      setSaving(false);
    }
  };

  // ---- Edit-mode helpers (operate on draft) ----
  const updateDraft = (k) => (e) => { const v = e.target.value; setDraft((d) => ({ ...d, [k]: v })); };
  const toggleDay = (day) => setDraft((d) => ({ ...d, meetingDays: d.meetingDays.includes(day) ? d.meetingDays.filter((x) => x !== day) : [...d.meetingDays, day] }));
  const updateLeader = (i, k) => (e) => { const v = e.target.value; setDraft((d) => ({ ...d, leaders: d.leaders.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)) })); };
  const addLeader = () => setDraft((d) => ({ ...d, leaders: [...d.leaders, emptyLeader()] }));
  const removeLeader = (i) => setDraft((d) => ({ ...d, leaders: d.leaders.filter((_, idx) => idx !== i) }));

  return (
    <AdminShell>
      <Link to="/admin/groups" className="back-link"><Icon.ArrowLeft size={14} />Back to school groups</Link>
      <div className="row" style={{ marginTop: 16, marginBottom: 32, gap: 16 }}>
        <h1 className="t-3xl" style={{ margin: 0, flex: 1 }}>{group.schoolName}</h1>
        <Badge variant={group.status}>{STATUS_LABEL[group.status] || group.status}</Badge>
      </div>

      <div className="dashboard-split" style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 24 }}>
        <div className="stack-lg" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {editing && draft ? (
            <>
              <Card>
                <h2 className="t-2xl" style={{ marginTop: 0, marginBottom: 20 }}>Group details</h2>
                <div className="stack-lg">
                  <Field label="School name" required htmlFor="ed-school">
                    <Input id="ed-school" value={draft.schoolName} onChange={updateDraft("schoolName")} />
                  </Field>
                  <Field label="Number of members" required htmlFor="ed-count">
                    <Input id="ed-count" type="number" min="0" value={draft.memberCount} onChange={updateDraft("memberCount")} />
                  </Field>
                  <Field label="Meeting days">
                    <div className="chips" style={{ marginTop: 4 }}>
                      {DAYS.map((day) => (
                        <button type="button" key={day} className={`chip ${draft.meetingDays.includes(day) ? "active" : ""}`} aria-pressed={draft.meetingDays.includes(day)} onClick={() => toggleDay(day)}>{day}</button>
                      ))}
                    </div>
                  </Field>
                </div>
              </Card>
              <Card>
                <div className="row row--between" style={{ alignItems: "center", marginBottom: 16 }}>
                  <h2 className="t-2xl" style={{ margin: 0 }}>Leadership contacts</h2>
                </div>
                <div className="stack-md" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {draft.leaders.map((leader, i) => (
                    <div key={i} style={{ border: "1px solid var(--border)", borderRadius: "var(--radius-md)", padding: 16, background: "var(--bg-warm)" }}>
                      <div className="row row--between" style={{ alignItems: "center", marginBottom: 12 }}>
                        <div className="t-sm" style={{ fontWeight: 600 }}>Leader {i + 1}</div>
                        {draft.leaders.length > 1 && (
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeLeader(i)} style={{ color: "var(--danger)" }}><Icon.Trash size={14} />Remove</Button>
                        )}
                      </div>
                      <div className="leader-grid">
                        <Field label="Name" htmlFor={`ed-ld-name-${i}`}><Input id={`ed-ld-name-${i}`} value={leader.name} onChange={updateLeader(i, "name")} /></Field>
                        <Field label="Position" htmlFor={`ed-ld-pos-${i}`}><Input id={`ed-ld-pos-${i}`} value={leader.position} onChange={updateLeader(i, "position")} /></Field>
                        <Field label="Email" htmlFor={`ed-ld-email-${i}`}><Input id={`ed-ld-email-${i}`} type="email" value={leader.email} onChange={updateLeader(i, "email")} /></Field>
                        <Field label="Phone" htmlFor={`ed-ld-phone-${i}`}><Input id={`ed-ld-phone-${i}`} value={leader.phone} onChange={updateLeader(i, "phone")} /></Field>
                        <Field label="Occupation" htmlFor={`ed-ld-occ-${i}`} className="field--span-2"><Input id={`ed-ld-occ-${i}`} value={leader.occupation} onChange={updateLeader(i, "occupation")} /></Field>
                      </div>
                    </div>
                  ))}
                </div>
                <Button type="button" variant="secondary" size="sm" onClick={addLeader} style={{ marginTop: 12 }}><Icon.Plus size={14} />Add another leader</Button>
              </Card>
            </>
          ) : (
            <>
              <Card>
                <h2 className="t-2xl" style={{ marginTop: 0, marginBottom: 20 }}>Group details</h2>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                  <DetailRow label="School" value={group.schoolName} bold />
                  <DetailRow label="Members" value={group.memberCount} />
                  <DetailRow label="Meeting days" value={(group.meetingDays || []).join(", ") || "—"} />
                </div>
              </Card>
              <Card>
                <h2 className="t-2xl" style={{ marginTop: 0, marginBottom: 16 }}>Leadership contacts</h2>
                <div className="stack-md" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {(group.leaders || []).map((l, i) => (
                    <div key={i} style={{ border: "1px solid var(--border)", borderRadius: "var(--radius-md)", padding: 16 }}>
                      <div className="row row--between" style={{ alignItems: "center", marginBottom: 8 }}>
                        <div className="t-base" style={{ fontWeight: 600 }}>{l.name}</div>
                        {l.position && <Badge variant="minor" noDot>{l.position}</Badge>}
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                        <DetailRow label="Email" value={l.email ? <a href={`mailto:${l.email}`}>{l.email}</a> : "—"} />
                        <DetailRow label="Phone" value={l.phone ? <a href={`tel:${l.phone}`}>{l.phone}</a> : "—"} />
                        <DetailRow label="Occupation" value={l.occupation || "—"} />
                      </div>
                    </div>
                  ))}
                  {(!group.leaders || group.leaders.length === 0) && <p className="t-sm muted" style={{ margin: 0 }}>No leadership contacts on record.</p>}
                </div>
              </Card>
              <Card>
                <h2 className="t-2xl" style={{ marginTop: 0, marginBottom: 16 }}>Submission</h2>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                  <DetailRow label="Submitted at" value={formatDateTime(group.submittedAt)} />
                  {group.reviewedAt && <DetailRow label="Reviewed at" value={formatDateTime(group.reviewedAt)} />}
                </div>
              </Card>
            </>
          )}
        </div>

        <div className="stack-lg" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {editing ? (
            <Card>
              <h3 className="t-base" style={{ margin: 0, marginBottom: 12, fontWeight: 600 }}>Editing</h3>
              <div className="stack-md" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <Button variant="primary" onClick={saveEdit} loading={saving} className="btn--full">{saving ? "Saving…" : "Save changes"}</Button>
                <Button variant="ghost" onClick={cancelEdit} className="btn--full">Cancel</Button>
              </div>
            </Card>
          ) : (
            <>
              <Card>
                <h3 className="t-base" style={{ margin: 0, marginBottom: 8, fontWeight: 600 }}>Review</h3>
                <p className="t-sm muted" style={{ margin: "0 0 12px" }}>
                  {group.status === "pending" ? "Approve to officially register this group, or reject the submission."
                    : group.status === "approved" ? "This group is officially registered."
                    : "This submission was rejected."}
                </p>
                <div className="stack-md" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {group.status !== "approved" && <Button variant="primary" onClick={() => setStatus("approved")} className="btn--full"><Icon.Check size={14} />Approve</Button>}
                  {group.status !== "rejected" && <Button variant="secondary" onClick={() => setStatus("rejected")} className="btn--full"><Icon.X size={14} />Reject</Button>}
                  {group.status !== "pending" && <Button variant="ghost" onClick={() => setStatus("pending")} className="btn--full">Move back to pending</Button>}
                </div>
              </Card>
              <Card>
                <h3 className="t-base" style={{ margin: 0, marginBottom: 12, fontWeight: 600 }}>Manage</h3>
                <div className="stack-md" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <Button variant="secondary" onClick={startEdit} className="btn--full"><Icon.Pencil size={14} />Edit details</Button>
                  <Button variant="ghost" onClick={() => setShowDelete(true)} style={{ color: "var(--danger)" }} className="btn--full"><Icon.Trash size={14} />Delete group</Button>
                </div>
              </Card>
            </>
          )}
        </div>
      </div>

      <ConfirmDialog open={showDelete} title="Delete this group?" body="The registration will be permanently removed." confirmLabel="Delete" destructive onConfirm={doDelete} onClose={() => setShowDelete(false)} />
    </AdminShell>
  );
}

export default AdminSchoolGroupsListPage;
